import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'

import type { SessionDetails } from '@/bindings/SessionDetails'
import { Button } from '@/components/button'
import { ConfirmButton } from '@/components/confirm'
import { Field, Select } from '@/components/field'
import { FormError } from '@/components/form-error'
import { PrintIcon } from '@/components/icons'
import { Card, Page } from '@/components/layout'
import { allTablesQuery, backOfficeKeys, changeTable, closeSession, sessionQuery } from '@/features/back-office/api'
import { BackLink } from '@/features/back-office/back-link'
import { SESSION_PATH } from '@/features/back-office/sessions-page'
import { SessionTicket } from '@/features/back-office/tickets'
import { LiveIndicator } from '@/features/ordering/order-view'
import { claim } from '@/lib/api'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, printPage, useBranchEvents } from '@/lib/live'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/sessions/$sessionId'
const SESSIONS_PATH = '/$restaurantSlug/$branchSlug/manage/sessions'

interface Scope {
  restaurantSlug: string
  branchSlug: string
  restaurantId: string
  branchId: string
  sessionId: string
}

/**
 * One dine-in session for staff (PRD, G11): every order at the table as a ticket with its
 * status, the bill, a print button (an 80 mm thermal ticket, G14), close (T16: only once no
 * order is active) and change table — a busy table offers a merge, after which the page
 * follows the orders to the target session. Every answer is the server's (T8); live through
 * G5's hints, polling behind them.
 */
export function SessionDetailsPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const { sessionId } = useParams({ from: ROUTE_ID })
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const session = useQuery(sessionQuery(restaurantId, branchId, sessionId))
  const [changing, setChanging] = useState(false)
  const branch = context?.branches.find((candidate) => candidate.id === branchId)
  const live = useBranchEvents(context && branch ? branchEventsUrl(context.settings.slug, branch.slug) : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.session(restaurantId, branchId, sessionId) })
  })

  const scope: Scope = { restaurantSlug, branchSlug, restaurantId, branchId, sessionId }
  const back = <BackLink to={SESSIONS_PATH} params={{ restaurantSlug, branchSlug }} />
  if (session.isPending) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {back}
        <p className="text-muted text-sm">{t('common.loading')}</p>
      </Page>
    )
  }
  if (!session.data) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {back}
        {claim(session.error, 'NOT_FOUND') ? (
          <p role="alert" className="text-muted text-sm">
            {t('sessions.notFound')}
          </p>
        ) : (
          <>
            <FormError error={session.error} />
            <Button variant="secondary" onClick={() => void session.refetch()} className="self-start">
              {t('common.retry')}
            </Button>
          </>
        )}
      </Page>
    )
  }

  // The server's answer replaces the session at once (T8); the list and the tickets follow behind.
  function take(updated: SessionDetails) {
    queryClient.setQueryData(backOfficeKeys.session(restaurantId, branchId, updated.session.id), updated)
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.sessions(restaurantId, branchId) })
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(restaurantId, branchId) })
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.orders(restaurantId, branchId) })
    // A merge answers the target session: the orders live there now, so does the page.
    if (updated.session.id !== sessionId) {
      void navigate({ to: SESSION_PATH, params: { restaurantSlug, branchSlug, sessionId: updated.session.id }, replace: true })
    }
  }
  function stale() {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.sessions(restaurantId, branchId) })
  }
  const open = session.data.session.closed_at === null

  return (
    <Page width="md" className="flex flex-col gap-4">
      {back}
      <SessionTicket details={session.data} main={context.settings.main_language} venue={{ restaurant: context.settings.name, branch: branch?.name, address: branch?.address, phone: branch?.phone }} scope={scope} />
      <LiveIndicator status={live} />
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <span className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={printPage}>
            <PrintIcon className="size-4" />
            {t('orderDetails.print')}
          </Button>
          {open && (
            <Button size="sm" variant="secondary" aria-expanded={changing} onClick={() => setChanging((value) => !value)}>
              {t('sessions.changeTable')}
            </Button>
          )}
        </span>
        {open ? <CloseButton scope={scope} details={session.data} onClosed={take} onStale={stale} /> : <span className="text-muted text-sm">{t('sessions.closedNotice')}</span>}
      </div>
      {open && changing && <ChangeTablePanel scope={scope} details={session.data} onMoved={take} onStale={stale} />}
    </Page>
  )
}

interface ActionProps {
  scope: Scope
  details: SessionDetails
  onStale: () => void
}

/**
 * Close behind a second press. The button is off while the page shows active orders (T16);
 * the server judges again under the lock, so a stale page hears the count or that the
 * session is already closed, and refetches either way.
 */
function CloseButton({ scope, details, onClosed, onStale }: ActionProps & { onClosed: (updated: SessionDetails) => void }) {
  const t = useT()
  const closing = useMutation({
    mutationFn: () => closeSession(scope.restaurantId, scope.branchId, scope.sessionId),
    onSuccess: onClosed,
    onError: (error) => {
      if (claim(error, 'SESSION_CLOSED', 'SESSION_HAS_ACTIVE_ORDERS')) onStale()
    },
  })
  const blocked = details.active_count > 0
  return (
    <span className="flex flex-col items-end gap-1">
      <ConfirmButton
        label={t('sessions.close')}
        variant="secondary"
        disabled={blocked}
        confirmLabel={t('sessions.closeConfirm')}
        keepLabel={t('sessions.closeKeep')}
        align="end"
        confirm={() => closing.mutateAsync()}
      />
      {blocked && <span className="text-muted text-sm">{t('sessions.closeBlocked', { count: details.active_count })}</span>}
      <FormError error={closing.error} />
    </span>
  )
}

/** Pick another table; a busy one is named and offers the merge instead. */
function ChangeTablePanel({ scope, details, onMoved, onStale }: ActionProps & { onMoved: (updated: SessionDetails) => void }) {
  const t = useT()
  const tables = useQuery(allTablesQuery(scope.restaurantId, scope.branchId))
  const [tableId, setTableId] = useState('')
  const moving = useMutation({
    mutationFn: (merge: boolean) => changeTable(scope.restaurantId, scope.branchId, scope.sessionId, { table_id: tableId, merge }),
    onSuccess: onMoved,
    onError: (error) => {
      if (claim(error, 'SESSION_CLOSED')) onStale()
    },
  })
  const busy = claim(moving.error, 'TABLE_BUSY')
  const others = (tables.data ?? []).filter((table) => table.id !== details.session.table_id)
  const target = others.find((table) => table.id === tableId)

  if (tables.isPending) return <p className="text-muted text-sm print:hidden">{t('common.loading')}</p>
  if (!tables.data) return <FormError error={tables.error} />
  if (others.length === 0) return <p className="text-muted text-sm print:hidden">{t('sessions.noOtherTables')}</p>
  return (
    <Card className="flex flex-col gap-3 print:hidden">
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (target) moving.mutate(false)
        }}
      >
        <Field label={t('sessions.target')}>
          <Select
            value={tableId}
            onChange={(event) => {
              setTableId(event.target.value)
              moving.reset()
            }}
          >
            <option value="">—</option>
            {others.map((table) => (
              <option key={table.id} value={table.id}>
                {table.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" size="sm" disabled={!target || busy !== undefined} loading={moving.isPending && !moving.variables}>
          {t('sessions.move')}
        </Button>
      </form>
      {busy && target ? (
        <div className="flex flex-col gap-2">
          <p role="status" className="text-warning text-sm">
            {t('sessions.busy', { name: String(busy.params.table_name) })}
          </p>
          <Button size="sm" variant="danger" className="self-start" loading={moving.isPending} onClick={() => moving.mutate(true)}>
            {t('sessions.merge', { name: target.name })}
          </Button>
        </div>
      ) : (
        <FormError error={moving.error} />
      )}
    </Card>
  )
}
